import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { createHead } from "@unhead/vue/client";

// Self-hosted variable fonts. Not Google Fonts: this portal is served from the
// customer's own deployment, and a third-party font request would be both a
// privacy leak and a hard dependency on a CDN being reachable from wherever it
// is installed.
import "@fontsource-variable/space-grotesk";
import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";

import "./styles/tokens.css";
import "./styles/base.css";

import App from "./App.vue";
import { i18n, applyInitialLocale } from "./i18n";
import { session, restore } from "./lib/session";

import SignIn from "./views/SignIn.vue";
import SignUp from "./views/SignUp.vue";
import VerifyEmail from "./views/VerifyEmail.vue";
import AcceptInvite from "./views/AcceptInvite.vue";
import Security from "./views/Security.vue";
import Team from "./views/Team.vue";
import Notices from "./views/Notices.vue";
import Apps from "./views/Apps.vue";
import AppEditor from "./views/AppEditor.vue";
import Provider from "./views/Provider.vue";
import Webhooks from "./views/Webhooks.vue";
import Docs from "./views/Docs.vue";
import Guides from "./views/Guides.vue";
import Changelog from "./views/Changelog.vue";
import Console from "./views/Console.vue";

const routes = [
  { path: "/", redirect: "/apps" },

  { path: "/sign-in", name: "sign-in", component: SignIn, meta: { public: true } },
  { path: "/sign-up", name: "sign-up", component: SignUp, meta: { public: true } },
  { path: "/verify", name: "verify", component: VerifyEmail, meta: { public: true } },
  { path: "/accept-invite", name: "accept-invite", component: AcceptInvite, meta: { public: true } },

  { path: "/apps", name: "apps", component: Apps },
  { path: "/apps/:id", name: "app", component: AppEditor, props: true },
  { path: "/webhooks", name: "webhooks", component: Webhooks },
  { path: "/security", name: "security", component: Security },
  { path: "/team", name: "team", component: Team },
  { path: "/notices", name: "notices", component: Notices },

  // "Provider" is the UI term; the hub also answers /organisation for the same resource.
  { path: "/provider", name: "provider", component: Provider },
  // Anyone with the old link, including this portal's own earlier builds.
  { path: "/organisation", redirect: "/provider" },

  /**
   * The documentation is PUBLIC. A developer evaluating the platform has to be
   * able to read the contract before they hold a credential — which is also why
   * openapi.json itself is unauthenticated. The console is public for the same
   * reason: it is useless without a token the visitor supplies themselves.
   */
  { path: "/docs", name: "docs", component: Docs, meta: { public: true } },
  {
    path: "/docs/reference",
    name: "reference",
    // Lazily loaded: Scalar is the largest thing this portal ships, and most
    // visits are to sign in and look at an app rather than to read the whole
    // reference.
    component: () => import("./views/Reference.vue"),
    // Rendered edge to edge — the reference has its own sidebar.
    meta: { public: true, wide: true }
  },
  { path: "/docs/guides", name: "guides", component: Guides, meta: { public: true } },
  { path: "/docs/changelog", name: "changelog", component: Changelog, meta: { public: true } },
  { path: "/console", name: "console", component: Console, meta: { public: true } },

  { path: "/:pathMatch(.*)*", redirect: "/apps" }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Land at the top on a new screen, but restore position on a back navigation
  // — returning to a long list you had scrolled and being thrown to the top is
  // the classic SPA annoyance.
  scrollBehavior(to, from, saved) {
    if (saved) return saved;
    if (to.hash) return { el: to.hash, top: 72 };
    return { top: 0 };
  }
});

router.beforeEach(async (to) => {
  if (!session.ready) await restore();
  if (to.meta.public) return true;
  if (!session.token) return { name: "sign-in", query: { next: to.fullPath } };
  // A self-signed-up user who has not registered a provider yet finishes that first.
  if (session.user && !session.developer && to.name !== "security") return { name: "sign-up" };
  return true;
});

/**
 * `createHead()` is registered for Scalar, not for us.
 *
 * `@scalar/api-reference` calls `useHead()` in its setup to manage the document
 * title while the reference is open. Without the plugin providing a context
 * that call THROWS, Vue swallows it as an unhandled setup error, and the
 * component renders nothing at all — an empty panel with the failure visible
 * only in the console. That is precisely how the reference came to look
 * "missing" rather than broken.
 */
const head = createHead();

applyInitialLocale();

createApp(App).use(router).use(i18n).use(head).mount("#app");
